/**
 * SKU derivation + normalization, shared by the API and all three SPAs.
 *
 * A SKU is the product's scannable identity: the POS matches a barcode scan by
 * full-string, case-insensitive SKU compare (`apps/cashier/src/lib/pos-search.ts`),
 * so these helpers keep generated codes short, uppercase and label-printable.
 *
 * `products.sku` and `product_variants.sku` are GLOBALLY unique per database —
 * house products, merchant products and every variant share one namespace.
 */

/** Max length of a sku column (matches the zod schemas on both create paths). */
export const SKU_MAX_LEN = 64;

/**
 * Prefixes the platform mints for itself. A merchant may not squat on these:
 * - `LEG-` legacy-import placeholder products (`packages/db/src/import/sales-engine.ts`)
 * - `KIT-` seeded kit products (`packages/db/src/seed-demo.ts`)
 * - `V-` / `M-` auto-generated variant + merchant fallbacks
 */
export const RESERVED_SKU_PREFIXES = ['LEG-', 'KIT-', 'V-', 'M-'] as const;

/**
 * The suffix the archive/restore rename appends so an archived product frees its
 * sku for reuse. Mirrors `ARCHIVED_SUFFIX_RE` in `apps/api/src/services/products.ts`
 * — an admin-typed sku must never collide with it, or the product can never be
 * archived properly (the rename short-circuits on an already-suffixed sku).
 *
 * Matched CASE-INSENSITIVELY here even though the rename writes exact-case
 * `-Archived`: operator input is uppercased before it reaches this check, and
 * uniqueness compares case-insensitively, so a typed `-ARCHIVED` would still
 * push the real archive rename onto the `-Archived-2` probe path.
 */
export const ARCHIVED_SKU_SUFFIX_RE = /-Archived(-\d+)?$/i;

/** Uppercase, trim, and collapse anything non-alphanumeric into single dashes. */
function slugSegment(value: string): string {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Derive an uppercase, alphanumeric-dashed SKU from a product name. */
export function skuFromName(name: string): string {
  return slugSegment(name);
}

/**
 * Canonical form of an admin/merchant-typed sku: trimmed + uppercased. We do NOT
 * strip punctuation here — an operator may legitimately type `ABC_123` or `A.1`,
 * and rewriting their input would be surprising. Uppercasing alone is what makes
 * the case-insensitive POS scan unambiguous.
 */
export function normalizeSku(sku: string): string {
  return sku.trim().toUpperCase();
}

/** True if `sku` starts with a platform-reserved prefix (case-insensitive). */
export function hasReservedSkuPrefix(sku: string): boolean {
  const s = normalizeSku(sku);
  return RESERVED_SKU_PREFIXES.some((p) => s.startsWith(p));
}

/** True if `sku` ends with the archive rename suffix. */
export function hasArchivedSkuSuffix(sku: string): boolean {
  return ARCHIVED_SKU_SUFFIX_RE.test(sku.trim());
}

/**
 * Build a readable variant sku from its parent product's sku plus the variation
 * option labels — `TSHIRT` + ['Red', 'Large'] → `TSHIRT-RED-LARGE`.
 *
 * This replaces the opaque `V-<uuid>` fallback for NEWLY created variants only.
 * Existing `V-*` skus are never rewritten: their labels are already printed.
 *
 * Returns null when nothing usable can be derived (blank parent sku, or no
 * labels) so the caller can fall back to the random generator.
 */
export function deriveVariantSku(parentSku: string, optionLabels: string[]): string | null {
  const base = slugSegment(parentSku);
  if (!base) return null;
  const segments = optionLabels.map(slugSegment).filter(Boolean);
  if (segments.length === 0) return null;

  let candidate = [base, ...segments].join('-');
  if (candidate.length <= SKU_MAX_LEN) return candidate;

  // Too long: shorten each option segment before touching the parent sku, so the
  // family prefix stays scannable/greppable.
  for (const maxSeg of [12, 8, 5, 3]) {
    candidate = [base, ...segments.map((s) => s.slice(0, maxSeg))].join('-');
    if (candidate.length <= SKU_MAX_LEN) return candidate;
  }
  return candidate.slice(0, SKU_MAX_LEN).replace(/-+$/, '');
}

/**
 * Append a numeric disambiguator to a sku while staying within `SKU_MAX_LEN`.
 * Used to probe `-2`, `-3`, … when a derived variant sku already exists.
 */
export function suffixSku(sku: string, n: number): string {
  const suffix = `-${n}`;
  const room = SKU_MAX_LEN - suffix.length;
  return `${sku.length > room ? sku.slice(0, room).replace(/-+$/, '') : sku}${suffix}`;
}
